/** A category configuration is a NAMING/ROLE layer: it captions the hierarchy rungs and carries the rule sets inference consumes, never attribute VALUES — those are always live from `useStudioAttributesQuery`. Naming resolves at read time from the saved profile and nothing here writes or activates. */
import {
  POG_ATTRIBUTE_CANDIDATES,
  type PogAdapterOptions,
} from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import type { AttributeCandidate } from "../domain/spineInference";
import { compileStudioProfile, type CompiledStudioItemMaster } from "./itemMasterWorkbook";
import { overlayAttrOf, type StudioProfile, type StudioSemanticRoles } from "./studioProfile";
import type { StudioAttributesData } from "./useStudioAttributesQuery";

/** The overlay attribute a `variants_restrictions` row matches: the backend matches `c.variant`, and `variant` IS CDT0, so the terminal-variant role must point here whatever the profile calls its terminal column. */
export const RESTRICTION_VARIANT_ATTR = "variant";

/** Value-rule maps re-keyed into the live namespace: compiled rules are keyed by PROFILE attribute keys, inference looks them up by live column name, and an untranslated key silently matches nothing. */
const overlayValueRulesOf = (
  profile: StudioProfile,
  rules: Readonly<Record<string, readonly string[]>> | undefined,
): Record<string, readonly string[]> | undefined =>
  rules &&
  Object.fromEntries(
    Object.entries(rules).map(([key, values]) => [overlayAttrOf(profile, key), values]),
  );

/** Level captions for the strip, tree and findings. A live attribute the profile does not name keeps the overlay's own label: silence about a column is not a rename. */
export const namedOverlayCandidates = (
  profile: StudioProfile,
  candidates: readonly AttributeCandidate[],
): AttributeCandidate[] => {
  const labelByAttr = new Map(
    profile.attributes.map((attribute) => [overlayAttrOf(profile, attribute.key), attribute.label]),
  );
  return candidates.map((candidate) => ({
    ...candidate,
    label: labelByAttr.get(candidate.attr) ?? candidate.label,
  }));
};

/** The profile's semantic roles in the live namespace. A role naming a column the live data lacks is DROPPED, so `toResolverInputs` sees an unconfigured roles list and falls back to its spine-derived default rather than reading "nothing carries block identity". */
export const overlayRolesOf = (
  profile: StudioProfile,
  candidates: readonly AttributeCandidate[],
): StudioSemanticRoles => {
  const live = new Set(candidates.map((candidate) => candidate.attr));
  const translate = (key: string): string | undefined => {
    const attr = overlayAttrOf(profile, key);
    return live.has(attr) ? attr : undefined;
  };
  const translateList = (keys: readonly string[]): string[] =>
    keys.map(translate).filter((attr): attr is string => attr !== undefined);
  const roles = profile.semanticRoles;
  return {
    blockIdentity: translateList(roles.blockIdentity),
    vertical: translateList(roles.vertical),
    brand: translate(roles.brand) ?? roles.brand,
    tier: translate(roles.tier) ?? roles.tier,
    quantity: translate(roles.quantity) ?? roles.quantity,
    terminalVariant: live.has(RESTRICTION_VARIANT_ATTR)
      ? RESTRICTION_VARIANT_ATTR
      : (translate(roles.terminalVariant) ?? roles.terminalVariant),
  };
};

/** Live attribute names the profile says are not a CDT level. */
export const ignoredAttributesOf = (profile: StudioProfile): string[] =>
  (profile.ignoredAttributes ?? []).map((attr) => attr.trim()).filter((attr) => attr.length > 0);

/** The studio's inputs for one category: LIVE attribute values, named and role-tagged by the active naming profile. */
export interface NamedStudioConfiguration extends CompiledStudioItemMaster {
  /** Always empty: values come from the live attributes API, never a stored workbook. */
  declaredValues: Readonly<Record<string, Readonly<Record<string, string | null>>>>;
  /** Live attribute → its configured caption, for naming the gap. */
  declaredLabels: Readonly<Record<string, string>>;
  /** The live attributes that may become a CDT level below cdt1 at Finalize
   *  (`StudioDraft.profileLevels`): the profile's declared attributes minus the ones the
   *  merchant removed (`ignoredAttributes`). */
  profileLevels: readonly string[];
}

/**
 * The profile as it applies to the live attribute table. A saved profile can name a column the
 * merchant's item master does not have: Sam's Club laundry's saved profile reads its
 * Subcategory from `subCategory`, and Sam's item master carries the subcategories in
 * `section`. A level whose column is not in the live data reads the live column that carries
 * the level's own key, when no other level of the profile reads that column. Otherwise the
 * level would show nothing, and the live column, captioned by its own name, would carry the
 * subcategories on a second level.
 */
export const bridgedToLiveData = (
  profile: StudioProfile,
  liveAttrs: ReadonlySet<string>,
): StudioProfile => {
  const claimed = new Set(
    profile.attributes.map((attribute) => overlayAttrOf(profile, attribute.key)),
  );
  return {
    ...profile,
    attributes: profile.attributes.map((attribute) => {
      const attr = overlayAttrOf(profile, attribute.key);
      if (attr === attribute.key || liveAttrs.has(attr)) return attribute;
      if (!liveAttrs.has(attribute.key) || claimed.has(attribute.key)) return attribute;
      return { ...attribute, overlayAttr: attribute.key };
    }),
  };
};

export const namedStudioConfigurationOf = (
  savedProfile: StudioProfile,
  attributes: StudioAttributesData,
): NamedStudioConfiguration => {
  const liveCandidates = attributes.adapterOptions.overlayCandidates ?? POG_ATTRIBUTE_CANDIDATES;
  // `variant` is CDT 0 on every planogram item, whether or not the item master lists it. A
  // column the merchant ignored is no level, so no level is bridged onto it.
  const ignored = new Set(ignoredAttributesOf(savedProfile));
  const profile = bridgedToLiveData(
    savedProfile,
    new Set(
      [RESTRICTION_VARIANT_ATTR, ...liveCandidates.map((candidate) => candidate.attr)].filter(
        (attr) => !ignored.has(attr),
      ),
    ),
  );
  const compiled = compileStudioProfile(profile);
  // Ignored attributes never become candidates, so no level is built on them; the overlay still carries their values.
  const seen = new Set<string>(ignoredAttributesOf(profile));
  const fromProfile: AttributeCandidate[] = [];
  for (const attribute of profile.attributes) {
    const attr = overlayAttrOf(profile, attribute.key);
    if (seen.has(attr)) continue;
    seen.add(attr);
    fromProfile.push({ attr, label: attribute.label });
  }
  // A level the profile reads through an overlay (Size through the size tier) is one level: the
  // raw column behind it is not a second candidate. Left in, the 78 sizes in ounces on base
  // planogram 210 won the rung under the brand and put Tide's liquids among its pods.
  const shadowed = new Set<string>(
    profile.attributes.flatMap((attribute) =>
      attribute.pogField !== undefined &&
      attribute.overlayAttr !== undefined &&
      attribute.overlayAttr !== attribute.pogField
        ? [attribute.pogField]
        : [],
    ),
  );
  const candidates = namedOverlayCandidates(profile, [
    ...fromProfile,
    ...liveCandidates.filter(
      (candidate) => !seen.has(candidate.attr) && !shadowed.has(candidate.attr),
    ),
  ]);
  /* Only the levels the profile names carry a configured order. A live column the profile is
     silent about is still a candidate, but its place in the attributes response is not a
     merchant's ranking: inference orders those by the evidence the base gives. */
  const preferredOrder = fromProfile.map((candidate) => candidate.attr);
  const semanticRoles = overlayRolesOf(profile, candidates);
  const normalization = attributes.adapterOptions.normalization;
  const adapterOptions: PogAdapterOptions = {
    ...attributes.adapterOptions,
    overlayCandidates: candidates,
    // Resolved per item by the adapter, so the candidates above are semantic names by the time inference runs.
    sectionSemantics: profile.sectionSemantics,
    sectionAttr: RESTRICTION_VARIANT_ATTR,
    // The only normalization knob the naming layer owns; an explicit `false` stands.
    normalization:
      normalization === false
        ? false
        : { ...normalization, placeholderSpellings: profile.missingValues },
  };
  return {
    ...compiled,
    adapterOptions,
    pogAdapterOptions: adapterOptions,
    preferredOrder,
    inferenceOptions: {
      ...compiled.inferenceOptions,
      preferredOrder,
      declaredLevelOrder: semanticRoles.blockIdentity,
      brandAttr: semanticRoles.brand,
      // CDT0 is the root by construction, not by measurement; asserted only when the live data carries the column.
      ...(candidates.some((candidate) => candidate.attr === RESTRICTION_VARIANT_ATTR)
        ? { rootAttr: RESTRICTION_VARIANT_ATTR }
        : {}),
      firstPreferredValues: overlayValueRulesOf(
        profile,
        compiled.inferenceOptions.firstPreferredValues,
      ),
      lastPreferredValues: overlayValueRulesOf(
        profile,
        compiled.inferenceOptions.lastPreferredValues,
      ),
    },
    aestheticsRules: {
      ...compiled.aestheticsRules,
      brandAttr: semanticRoles.brand,
      tierAttr: semanticRoles.tier,
      sizeAttr: semanticRoles.quantity,
      flavourAttr: semanticRoles.terminalVariant,
      verticalAttrs: semanticRoles.vertical,
    },
    semanticRoles,
    declaredValues: {},
    declaredLabels: Object.fromEntries(
      profile.attributes.map((attribute) => [
        overlayAttrOf(profile, attribute.key),
        attribute.label,
      ]),
    ),
    profileLevels: profileLevelsOf(profile),
  };
};

/** What may be a tier: the profile's declared attributes (live names), minus what the
 *  merchant removed (`ignoredAttributes`). */
export const profileLevelsOf = (profile: StudioProfile): string[] => {
  const ignored = new Set(ignoredAttributesOf(profile));
  return [
    ...new Set(profile.attributes.map((attribute) => overlayAttrOf(profile, attribute.key))),
  ].filter((attr) => !ignored.has(attr));
};
